import * as Scrivito from "scrivito";
import React, { useCallback, useMemo } from "react";
import {
  Tree,
  type DragPreviewProps,
  type NodeRendererProps,
} from "@hogartrabe/react-arborist-vrh";
import type { RowHeightCalculatorParams } from "@hogartrabe/react-arborist-vrh/dist/module/types/tree-props";
import useResizeObserver from "use-resize-observer";
import {
  useProjectTree,
  useTreeRef,
  useIsProjectLoading,
  getOpenMap,
  useProjectActions,
} from "stores/project";
import {
  useNavigateToCustomProduct,
  useNavigateToProduct,
} from "hooks/projectHooks";
import { XCircleLoader } from "components/XCircleLoader/XCircleLoader";
import { Node } from "./components/Node";
import { disableDrop, isDragDisabled } from "utils";
import { defaultOptions } from "variables/nodeDefaultOptions";
import {
  useCalculateRowHeight,
  useRedraw,
  useSmoothScroll,
  useProjectTreeContentTranslations,
  useRedirectOnError,
} from "hooks/projectTreeHooks";
import { DraggedFolder } from "./components/Folder/DraggedFolder";
import { useLanguage } from "stores/globalStore";
import type { Node as NodeType } from "interfaces/schemas";

type ProjectTreeProps = { options?: Partial<typeof defaultOptions> };

export const ProjectTree = Scrivito.connect(
  ({ options = {} }: ProjectTreeProps) => {
    const treeRef = useTreeRef();
    const projectTree = useProjectTree();
    const isLoading = useIsProjectLoading();
    const actions = useProjectActions();

    useRedirectOnError();

    const lang = useLanguage();
    // Translations needed for the project tree, fetched here instead of in the Node component to avoid unnecessary translate calls.
    const translations = useProjectTreeContentTranslations();
    // Default options for the project tree, which can be overridden by the options prop.
    const treeOptions = useMemo(
      () => ({ ...defaultOptions, ...options }),
      [options],
    );

    const navigateToProduct = useNavigateToProduct();
    const navigateToCustomProduct = useNavigateToCustomProduct();
    // resize observer for providing the correct width and height to the tree component.
    const { ref, width, height } = useResizeObserver();

    // Custom hook for animating the smooth scroll-to behaviour
    const { setScrollToIndex, setScrollOffset } = useSmoothScroll({
      windowHeight: height ?? 0,
    });
    // Custom hook for node actions, which provides the actions for the nodes in the project tree.

    // Function for ensuring that the node heights are correct after the project tree is updated.
    // It redraws the list, recalculating the heights of the nodes from the index that is passed to it.
    useRedraw();

    const childrenAccessor = useCallback((d: NodeType) => d.nodes, []);
    const calculateRowHeight = useCalculateRowHeight(treeOptions);
    const rowHeight = useCallback(
      (node: RowHeightCalculatorParams<NodeType>): number =>
        calculateRowHeight(node),
      [calculateRowHeight],
    );

    const RenderNode = useCallback(
      (props: NodeRendererProps<NodeType>) => (
        <Node
          {...props}
          options={treeOptions}
          translations={translations}
          setScrollToIndex={setScrollToIndex}
          navigateToProduct={navigateToProduct}
          lang={lang}
          navigateToCustomProduct={navigateToCustomProduct}
        />
      ),
      [
        lang,
        navigateToCustomProduct,
        navigateToProduct,
        setScrollToIndex,
        translations,
        treeOptions,
      ],
    );

    const RenderDragPreview = useCallback(
      (props: DragPreviewProps) => {
        if (!props.id || !props.mouse || !props.isDragging || !treeRef) {
          return null;
        }

        const draggedNode = treeRef.current?.get?.(props.id);
        const draggedData = draggedNode?.data;
        if (!draggedNode || !draggedData) {
          return null;
        }

        return (
          <div
            style={{
              left: props.mouse.x + 8,
              top: props.mouse.y - 50,
            }}
            className="position-fixed z-index-9999 w-max-content"
          >
            <DraggedFolder node={draggedNode} />
          </div>
        );
      },
      [treeRef],
    );

    const disableDrag = useMemo(
      () =>
        treeOptions.dndDisabled
          ? true
          : (node: NodeType) => isDragDisabled(node),
      [treeOptions.dndDisabled],
    );

    const onScroll = useCallback(
      ({ scrollOffset }: { scrollOffset: number }) =>
        setScrollOffset(scrollOffset),
      [setScrollOffset],
    );

    // This boolean is used to determine if the project tree should be shown.

    const showTree = projectTree && getOpenMap() && height && width;

    return (
      <div className="w-100 h-100 pb-4" ref={ref}>
        {isLoading && (
          <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <XCircleLoader />
          </div>
        )}

        {showTree && !isLoading && (
          <Tree
            data={projectTree}
            initialOpenState={getOpenMap() ?? undefined}
            childrenAccessor={childrenAccessor}
            onMove={actions.tree.moveNode}
            indent={treeOptions.indentSize}
            width={width}
            height={height}
            ref={treeRef}
            rowHeight={rowHeight}
            disableDrag={disableDrag}
            renderDragPreview={RenderDragPreview}
            disableDrop={disableDrop}
            overscanCount={treeOptions.overscanCount}
            onScroll={onScroll}
          >
            {RenderNode}
          </Tree>
        )}
      </div>
    );
  },
);
